import { useState, useEffect, useRef, useCallback } from 'react';
import QRCode from 'qrcode';

const C = {
  green:"#16320f",greenLight:"#1f4516",greenMid:"#2a5c1e",greenBorder:"#3a7029",
  gold:"#d9c20d",bg:"#0c1e09",surface:"#111f0d",surface2:"#162b11",
  text:"#e8f0e6",textMid:"#8faa8a",textDim:"#4a6646",teal:"#34d399",
  red:"#ef4444",blue:"#60a5fa",
};
const AGE_GROUP_COLORS = {"10U":"#34d399","12U":"#d9c20d","14U":"#60a5fa","16U":"#f97316","18U":"#a78bfa"};
const STATE_ABBR = {Alabama:"AL",Alaska:"AK",Arizona:"AZ",Arkansas:"AR",California:"CA",Colorado:"CO",Connecticut:"CT",Delaware:"DE","District of Columbia":"DC",Florida:"FL",Georgia:"GA",Hawaii:"HI",Idaho:"ID",Illinois:"IL",Indiana:"IN",Iowa:"IA",Kansas:"KS",Kentucky:"KY",Louisiana:"LA",Maine:"ME",Maryland:"MD",Massachusetts:"MA",Michigan:"MI",Minnesota:"MN",Mississippi:"MS",Missouri:"MO",Montana:"MT",Nebraska:"NE",Nevada:"NV","New Hampshire":"NH","New Jersey":"NJ","New Mexico":"NM","New York":"NY","North Carolina":"NC","North Dakota":"ND",Ohio:"OH",Oklahoma:"OK",Oregon:"OR",Pennsylvania:"PA","Rhode Island":"RI","South Carolina":"SC","South Dakota":"SD",Tennessee:"TN",Texas:"TX",Utah:"UT",Vermont:"VT",Virginia:"VA",Washington:"WA","West Virginia":"WV",Wisconsin:"WI",Wyoming:"WY"};
const AGE_GROUP_ORDER = {"10U":0,"12U":1,"14U":2,"16U":3,"18U":4};
const SEASON_LABELS = ["Spring","Summer","Fall","Winter"];
const DERIVED_STATS = new Set(["AVG","OBP","OPS","SLG","ERA","WHIP","FPCT","SBpct","BAA"]);
const STAT_LABELS = {"KL":"K-L","SBpct":"SB%","P":"#P"};
const BAT_FIELDS = ["GP","PA","AB","H","1B","2B","3B","HR","RBI","R","BB","K","KL","HBP","SAC","SF","ROE","FC","SB","CS","AVG","OBP","SLG","OPS","SBpct"];
const PIT_FIELDS = ["IP","GP","GS","BF","P","W","L","SV","H","R","ER","BB","K","KL","HBP","ERA","WHIP","LOB","WP","BAA"];
const FLD_FIELDS = ["TC","A","PO","FPCT","E","DP"];
// Mirrors ODM_FIELDS in src/App.jsx exactly (no shared import between these two files) — see that
// file's comment for the full ODM Testing / Allister Index background.
const ODM_FIELDS=[
  ["tenYardDash","10 YD DASH"],["twentyYardDash","20 YD DASH"],["fortyYardDash","40 YD DASH"],
  ["proAgilityShuttle","PRO AGILITY SHUTTLE"],["batSpeed","BAT SPEED"],["handSpeed","HAND SPEED"],
  ["timeToImpact","TIME TO IMPACT"],["ballExitSpeed","BALL EXIT SPEED"],["throwing","THROWING"],
  ["broadJump","BROAD JUMP"],["gripRight","GRIP RIGHT"],["gripLeft","GRIP LEFT"],
];

function addIP(a, b) {
  const f1=Math.floor(a),o1=Math.round((a-f1)*10),f2=Math.floor(b),o2=Math.round((b-f2)*10),tot=o1+o2;
  return (f1+f2+Math.floor(tot/3))+(tot%3)/10;
}
function ipToInnings(ip) { if(!ip)return 0;const f=Math.floor(ip),o=Math.round((ip-f)*10);return f+o/3; }

function normalizeSnaps(data) {
  if (!data) return [];
  if (Array.isArray(data)) return data;
  if (data.batting || data.pitching || data.fielding) return [{...data, gcTeam: data.gcTeam||"Legacy"}];
  return [];
}

function combineSnapshots(snapshots) {
  if (!snapshots?.length) return {batting:{},pitching:{},fielding:{}};
  const sum=(key,field)=>{const vals=snapshots.map(s=>s[key]?.[field]).filter(v=>v!=null&&!isNaN(Number(v)));return vals.length?vals.reduce((a,b)=>a+Number(b),0):null;};
  const b={};
  ['GP','PA','AB','H','1B','2B','3B','HR','RBI','R','BB','K','KL','HBP','SAC','SF','ROE','FC','SB','CS'].forEach(f=>{b[f]=sum('batting',f);});
  if(b.AB>0)b.AVG=+(b.H/b.AB).toFixed(3);
  const obpDen=(b.AB||0)+(b.BB||0)+(b.HBP||0)+(b.SF||0);
  if(obpDen>0)b.OBP=+(((b.H||0)+(b.BB||0)+(b.HBP||0))/obpDen).toFixed(3);
  if(b.AB>0)b.SLG=+(((b['1B']||0)+2*(b['2B']||0)+3*(b['3B']||0)+4*(b.HR||0))/b.AB).toFixed(3);
  if(b.OBP!=null&&b.SLG!=null)b.OPS=+(b.OBP+b.SLG).toFixed(3);
  if((b.SB||0)+(b.CS||0)>0)b.SBpct=+(b.SB/((b.SB||0)+(b.CS||0))).toFixed(3);
  const p={};
  ['GP','GS','BF','P','W','L','SV','H','R','ER','BB','K','KL','HBP','LOB','WP'].forEach(f=>{p[f]=sum('pitching',f);});
  const ipVals=snapshots.map(s=>s.pitching?.IP).filter(v=>v!=null&&!isNaN(Number(v))).map(Number);
  p.IP=ipVals.length?ipVals.reduce((a,b)=>addIP(a,b),0):null;
  const ipIn=ipToInnings(p.IP||0);
  if(ipIn>0)p.ERA=+((p.ER||0)/ipIn*7).toFixed(2);
  if(ipIn>0)p.WHIP=+(((p.BB||0)+(p.H||0))/ipIn).toFixed(2);
  if(p.BF>0)p.BAA=+((p.H||0)/p.BF).toFixed(3);
  const f2={};
  ['TC','A','PO','E','DP'].forEach(f=>{f2[f]=sum('fielding',f);});
  if((f2.TC||0)>0)f2.FPCT=+((f2.TC-(f2.E||0))/f2.TC).toFixed(3);
  return {batting:b, pitching:p, fielding:f2};
}

function fmtStatVal(lbl, val) {
  if (val==null) return '—';
  const PCT3=new Set(["AVG","OBP","OPS","SLG","SBpct","FPCT","BAA"]);
  const DEC2=new Set(["ERA","WHIP"]);
  if (PCT3.has(lbl)) { if(!val||Number(val)===0)return'—'; return Number(val).toFixed(3).replace(/^0\./,'.'); }
  if (DEC2.has(lbl)) return Number(val).toFixed(2);
  return val;
}

function fmtDateRange(start, end) {
  if (!start) return '';
  const opts = { month: 'short', day: 'numeric' };
  const optsY = { month: 'short', day: 'numeric', year: 'numeric' };
  const [sy,sm,sd] = start.split('-').map(Number);
  const s = new Date(sy,sm-1,sd);
  if (!end||end===start) return s.toLocaleDateString('en-US',optsY);
  const [ey,em,ed] = end.split('-').map(Number);
  const e = new Date(ey,em-1,ed);
  return `${s.toLocaleDateString('en-US',opts)} – ${e.toLocaleDateString('en-US',optsY)}`;
}

function CollegeLogo({ logoUrl, name, size = 20 }) {
  if (!logoUrl) return null;
  return <img src={logoUrl} alt={name || ''} width={size} height={size} style={{ objectFit: 'contain', verticalAlign: 'middle', marginRight: 5, borderRadius: 2, flexShrink: 0 }} />;
}

function getVideoEmbed(url) {
  if (!url) return null;
  const ytMatch = url.match(/(?:youtube\.com\/(?:watch\?(?:.*&)?v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
  if (ytMatch) return { type: 'iframe', src: `https://www.youtube.com/embed/${ytMatch[1]}`, label: 'YouTube' };
  const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
  if (vimeoMatch) return { type: 'iframe', src: `https://player.vimeo.com/video/${vimeoMatch[1]}`, label: 'Vimeo' };
  if (url.includes('hudl.com')) return { type: 'link', src: url, label: 'Hudl' };
  return { type: 'link', src: url, label: 'Video' };
}

const MINI_FIELD_POS = {
  P:    { x: 65,  y: 70 },
  C:    { x: 65,  y: 99 },
  '1B': { x: 90,  y: 73 },
  '2B': { x: 78,  y: 57 },
  SS:   { x: 52,  y: 57 },
  '3B': { x: 40,  y: 73 },
  LF:   { x: 22,  y: 20 },
  CF:   { x: 65,  y: 11 },
  RF:   { x: 108, y: 20 },
};
function miniPosColor(pos) {
  if (pos === 'P') return '#34d399';
  if (pos === 'C') return '#60a5fa';
  if (['1B','2B','SS','3B'].includes(pos)) return '#d9c20d';
  if (['LF','CF','RF'].includes(pos)) return '#f97316';
  return '#a78bfa';
}
function MiniDiamond({ positions = [] }) {
  const W = 130, H = 118;
  const home = { x: 65, y: 102 };
  const first = { x: 96, y: 74 };
  const second = { x: 65, y: 46 };
  const third = { x: 34, y: 74 };
  const posSet = new Set(positions);
  return (
    <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} style={{ display: 'block', flexShrink: 0 }}>
      {/* Basepaths */}
      <polygon points={`${home.x},${home.y} ${first.x},${first.y} ${second.x},${second.y} ${third.x},${third.y}`} fill="none" stroke={C.greenBorder} strokeWidth={0.9} opacity={0.5}/>
      {/* Bases */}
      {[first, second, third].map((b, i) => (
        <rect key={i} x={b.x-4} y={b.y-4} width={8} height={8} fill={C.surface} stroke={C.greenBorder} strokeWidth={0.8} transform={`rotate(45,${b.x},${b.y})`}/>
      ))}
      {/* Home plate — point down (toward catcher) */}
      <polygon points={`${home.x},${home.y+5} ${home.x+4},${home.y+2} ${home.x+3},${home.y-3} ${home.x-3},${home.y-3} ${home.x-4},${home.y+2}`} fill={C.surface} stroke={C.greenBorder} strokeWidth={0.8}/>
      {/* Position spots */}
      {Object.entries(MINI_FIELD_POS).map(([pos, pt]) => {
        const active = posSet.has(pos);
        const color = active ? miniPosColor(pos) : C.textDim;
        const r = active ? 8 : 6;
        return (
          <g key={pos}>
            <circle cx={pt.x} cy={pt.y} r={r} fill={active ? color+'28' : 'none'} stroke={color} strokeWidth={active ? 1.5 : 0.7} opacity={active ? 1 : 0.28}/>
            <text x={pt.x} y={pt.y + 3.5} textAnchor="middle" fill={color} fontSize={active ? 5.5 : 5} fontFamily="DM Mono,monospace" fontWeight={active ? '600' : '400'} opacity={active ? 1 : 0.3}>{pos}</text>
          </g>
        );
      })}
    </svg>
  );
}

export default function RecruitProfile({ token }) {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statsSection, setStatsSection] = useState('batting');
  const [statsSeason, setStatsSeason] = useState('');
  const [statsAg, setStatsAg] = useState('');
  const [qrSrc, setQrSrc] = useState('');
  const [showPastTournaments, setShowPastTournaments] = useState(false);
  const [odmExpandedId, setOdmExpandedId] = useState(null);
  const [photoOpen, setPhotoOpen] = useState(false);
  const profileUrl = `${window.location.origin}/recruit/${token}`;
  const fromUrl = new URLSearchParams(window.location.search).get('from');
  const fromLabel = new URLSearchParams(window.location.search).get('label') || 'Back';

  useEffect(() => {
    if (!token) { setError('No profile token provided.'); setLoading(false); return; }
    fetch(`/api/recruit-profile?token=${encodeURIComponent(token)}`)
      .then(r => r.ok ? r.json() : r.json().then(e => Promise.reject(e.error || 'Profile not found')))
      .then(data => { setProfile(data); setLoading(false); })
      .catch(err => { setError(typeof err === 'string' ? err : 'This profile is not available.'); setLoading(false); });
  }, [token]);

  useEffect(() => {
    QRCode.toDataURL(profileUrl, { width: 200, margin: 1, errorCorrectionLevel: 'H' }).then(setQrSrc).catch(() => {});
  }, [profileUrl]);

  const downloadQr = async () => {
    const d = await QRCode.toDataURL(profileUrl, { width: 600, margin: 2, errorCorrectionLevel: 'H' });
    const a = document.createElement('a');
    a.href = d;
    a.download = `${(profile?.name||'player').replace(/ /g,'_')}_QR.png`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
  };

  // Stats derived values
  const statsData = profile?.stats || {};
  const seasons = profile?.seasons || [];
  const agesWithData = Object.keys(AGE_GROUP_ORDER).filter(ag =>
    Object.keys(statsData[ag]||{}).some(sid => normalizeSnaps(statsData[ag][sid]).length > 0)
  ).sort((a,b) => (AGE_GROUP_ORDER[a]||0)-(AGE_GROUP_ORDER[b]||0));

  // Prefer the age group(s) with data for the org's actual active season (same isActive lookup the
  // season-default fix, commit 0fd47c9, already added below) over agesWithData alone — the highest-
  // order bracket with ANY data at all can be a bracket whose only stats are from a season ago,
  // while the player's current-season stats sit under a different, lower-order bracket the season
  // logic never gets a chance to run for (confirmed on Piper Harris's real profile: 16U has only
  // Summer 2026 data, so it used to win by AGE_GROUP_ORDER alone even though her real Fall 2026
  // stats live under 14U). Among age groups that DO have current-season data, pick the highest-order
  // one — a player who guest-played multiple ages this season should default to her most advanced
  // placement. No age group has current-season data at all (nobody's imported anything yet this
  // season) → fall back to exactly today's behavior: highest-order bracket with any data.
  const orgActiveSeason = seasons.find(s => s.isActive);
  const agesWithActiveSeasonData = orgActiveSeason
    ? agesWithData.filter(ag => normalizeSnaps((statsData[ag]||{})[orgActiveSeason.id]).length > 0)
    : [];
  const activeAg = statsAg || (agesWithActiveSeasonData.length ? agesWithActiveSeasonData[agesWithActiveSeasonData.length-1] : agesWithData[agesWithData.length-1]) || '';
  // Object.keys() on statsData[activeAg] reflects whatever order seasons happened to get written
  // into this player's stats field — not chronological order — so both the season chip row and the
  // "which season is featured by default" pick used to be at the mercy of write order rather than
  // actual dates. Sort newest-first with the same idiom used everywhere else in this codebase for
  // season sorting (App.jsx SEASON_LABELS), then default to the org's actual active season if this
  // age group has data for it, falling back to the chronologically newest season this age group
  // does have data for otherwise (e.g. a played-up age group with no current-season stats yet).
  const seasonsForAg = Object.keys(statsData[activeAg]||{})
    .filter(sid => normalizeSnaps((statsData[activeAg]||{})[sid]).length > 0)
    .sort((sidA, sidB) => {
      const a = seasons.find(s => s.id === sidA) || {};
      const b = seasons.find(s => s.id === sidB) || {};
      return (b.year||0)-(a.year||0) || SEASON_LABELS.indexOf(b.season)-SEASON_LABELS.indexOf(a.season);
    });
  const defaultSeason = (orgActiveSeason && seasonsForAg.includes(orgActiveSeason.id)) ? orgActiveSeason.id : (seasonsForAg[0] || '');
  const activeSeason = statsSeason || defaultSeason;
  const snaps = normalizeSnaps((statsData[activeAg]||{})[activeSeason]);
  const { batting, pitching, fielding } = combineSnapshots(snaps);
  const hasStats = agesWithData.length > 0;
  const hasPitch = (pitching.IP||0) > 0 || (pitching.GP||0) > 0;
  const hasFld = (fielding.TC??0) > 0 || (fielding.A??0) > 0;
  // Check across all seasons for the active age group — avoids tabs appearing/disappearing per season
  const hasBatEver = seasonsForAg.some(sid => { const {batting:b} = combineSnapshots(normalizeSnaps((statsData[activeAg]||{})[sid])); return (b.GP||0)>0||(b.AB||0)>0; });
  const hasPitchEver = seasonsForAg.some(sid => { const {pitching:p} = combineSnapshots(normalizeSnaps((statsData[activeAg]||{})[sid])); return (p.IP||0)>0||(p.GP||0)>0; });
  const hasFldEver = seasonsForAg.some(sid => { const {fielding:f} = combineSnapshots(normalizeSnaps((statsData[activeAg]||{})[sid])); return (f.TC??0)>0||(f.A??0)>0; });
  const firstSection = hasBatEver ? 'batting' : hasPitchEver ? 'pitching' : 'fielding';
  // Fall back to first available section if selected section has no data
  const effectiveSection =
    statsSection==='batting'&&!hasBatEver ? (hasPitchEver?'pitching':'fielding') :
    statsSection==='pitching'&&!hasPitchEver ? (hasBatEver?'batting':'fielding') :
    statsSection==='fielding'&&!hasFldEver ? (hasBatEver?'batting':'pitching') :
    statsSection;
  const seasonLabel = (sid) => { const s = seasons.find(x => x.id === sid); return s ? `${s.season} ${s.year}` : sid; };
  // Org profile, per node (2026-10-20): the player's own node's org name (falling back to root's,
  // then the hardcoded default), resolved server-side by getRecruitingProfile and returned as
  // profile.org. profile can still be null here (header renders during the initial loading state,
  // before the fetch resolves), so this falls back the same way the header did before.
  const orgName = profile?.org?.name || "Warrior Academy Softball";

  return (
    <div style={{ minHeight: '100vh', background: C.bg, color: C.text, fontFamily: 'Inter,-apple-system,BlinkMacSystemFont,sans-serif' }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Bebas+Neue&family=DM+Mono:wght@400;500&family=Inter:wght@400;500;600&display=swap');
        *{box-sizing:border-box;}
        a{color:${C.teal};}
        .stat-chip{padding:3px 8px;border-radius:2px;cursor:pointer;font-size:10px;font-family:'DM Mono',monospace;letter-spacing:1px;border:1px solid;transition:all .12s;}
      `}</style>

      {/* Header */}
      <div style={{ background: C.greenLight, borderBottom: `2px solid ${C.gold}`, padding: '0 16px', display: 'flex', alignItems: 'center', gap: 10, minHeight: 56 }}>
        {fromUrl ? (
          <a href={fromUrl} style={{ display: 'flex', alignItems: 'center', gap: 6, minHeight: 44, padding: '0 10px 0 0', fontSize: 11, color: C.teal, textDecoration: 'none', fontFamily: "'DM Mono',monospace", letterSpacing: 1, flexShrink: 0, whiteSpace: 'nowrap' }}>
            <span style={{ fontSize: 16, lineHeight: 1 }}>←</span>
            {fromLabel.toUpperCase()}
          </a>
        ) : null}
        <img src="/warriors-logo.png" style={{ width: 32, height: 32, objectFit: 'contain', flexShrink: 0 }} alt={orgName} onError={e => { e.target.style.display='none'; }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: 20, letterSpacing: 4, color: C.gold, lineHeight: 1 }}>{orgName}</div>
          <div style={{ fontSize: 10, color: C.textDim, letterSpacing: 2 }}>ATHLETE PROFILE</div>
        </div>
      </div>

      <div style={{ maxWidth: 720, margin: '0 auto', padding: '28px 16px' }}>
        {loading && <div style={{ textAlign: 'center', padding: '80px 0', color: C.textDim, fontSize: 13 }}>Loading…</div>}

        {error && !loading && (
          <div style={{ textAlign: 'center', padding: '80px 0' }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>🔒</div>
            <div style={{ color: C.textDim, fontSize: 14, lineHeight: 1.7 }}>This recruiting profile is not publicly available.<br /><span style={{ fontSize: 12, opacity: 0.7 }}>{error}</span></div>
          </div>
        )}

        {profile && (
          <>
            {/* Committed banner */}
            {(() => {
              const committed = profile.committedSchool;
              return committed && (
                <div style={{ background: C.gold+'22', border: `1px solid ${C.gold}66`, borderRadius: 6, padding: '10px 16px', marginBottom: 14, display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ fontSize: 12, color: C.gold, fontWeight: 700, letterSpacing: 2, fontFamily: "'DM Mono',monospace" }}>COMMITTED</span>
                  <CollegeLogo logoUrl={committed.logoUrl} name={committed.name} size={22} />
                  <span style={{ fontSize: 16, color: C.text, fontWeight: 600 }}>{committed.name}</span>
                  {committed.division && <span style={{ fontSize: 11, color: C.textDim }}>({committed.division})</span>}
                </div>
              );
            })()}

            {/* Player header card */}
            <div style={{ background: C.surface, border: `1px solid ${C.greenBorder}`, borderRadius: 8, padding: 20, marginBottom: 14 }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14, flexWrap: 'wrap' }}>
                {/* Photo */}
                <div onClick={profile.photoUrl ? () => setPhotoOpen(true) : undefined} style={{ width: 96, height: 96, borderRadius: 6, overflow: 'hidden', background: C.bg, border: `1px solid ${C.greenBorder}`, flexShrink: 0, cursor: profile.photoUrl ? 'zoom-in' : 'default', position: 'relative' }}>
                  {profile.photoUrl ? (
                    <img src={profile.photoUrl} alt={profile.name} style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'top' }} onError={e => { e.target.style.display='none'; }} />
                  ) : (
                    <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <img src="/warriors-logo.png" style={{ width: 56, height: 56, objectFit: 'contain', opacity: 0.2 }} alt="" />
                    </div>
                  )}
                </div>
                {/* Photo lightbox */}
                {photoOpen && profile.photoUrl && (
                  <div onClick={() => setPhotoOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 300, background: '#000000ee', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'zoom-out' }}>
                    <img src={profile.photoUrl} alt={profile.name} style={{ maxWidth: '90vw', maxHeight: '90vh', objectFit: 'contain', borderRadius: 6, boxShadow: '0 8px 40px #00000088' }} onClick={e => e.stopPropagation()} />
                    <button onClick={() => setPhotoOpen(false)} style={{ position: 'absolute', top: 16, right: 20, background: 'none', border: 'none', color: '#fff', fontSize: 28, cursor: 'pointer', lineHeight: 1, opacity: 0.8 }}>✕</button>
                  </div>
                )}

                {/* Name + chips */}
                <div style={{ flex: 1, minWidth: 180 }}>
                  <div style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: 30, letterSpacing: 3, color: C.text, lineHeight: 1.1, marginBottom: 8 }}>{profile.name}</div>
                  <div style={{ marginBottom: 10 }}>
                    <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', marginBottom: profile.profilePositionNotes ? 5 : 0 }}>
                      {(profile.positions||[]).map(pos => <span key={pos} style={{ fontSize: 10, padding: '3px 9px', borderRadius: 3, border: `1px solid ${C.greenBorder}`, color: C.textMid, fontFamily: "'DM Mono',monospace", letterSpacing: 1 }}>{pos}</span>)}
                    </div>
                    {profile.profilePositionNotes && (
                      <div style={{ fontSize: 11, color: C.textDim, fontStyle: 'italic', lineHeight: 1.4 }}>{profile.profilePositionNotes}</div>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                    {profile.gradYear && <div><div style={{ fontSize: 9, color: C.textDim, letterSpacing: 1, fontFamily: "'DM Mono',monospace" }}>GRAD YEAR</div><div style={{ fontSize: 16, color: C.text, fontWeight: 600 }}>{profile.gradYear}</div></div>}
                    {profile.height && <div><div style={{ fontSize: 9, color: C.textDim, letterSpacing: 1, fontFamily: "'DM Mono',monospace" }}>HEIGHT</div><div style={{ fontSize: 16, color: C.text, fontWeight: 600 }}>{profile.height}</div></div>}
                    {profile.weight && <div><div style={{ fontSize: 9, color: C.textDim, letterSpacing: 1, fontFamily: "'DM Mono',monospace" }}>WEIGHT</div><div style={{ fontSize: 16, color: C.text, fontWeight: 600 }}>{profile.weight}</div></div>}
                    {(profile.bats||profile.throws) && <div><div style={{ fontSize: 9, color: C.textDim, letterSpacing: 1, fontFamily: "'DM Mono',monospace" }}>B/T</div><div style={{ fontSize: 16, color: C.text, fontWeight: 600 }}>{[profile.bats,profile.throws].filter(Boolean).join('/')}</div></div>}
                    {profile.gpa && <div><div style={{ fontSize: 9, color: C.textDim, letterSpacing: 1, fontFamily: "'DM Mono',monospace" }}>GPA</div><div style={{ fontSize: 16, color: C.text, fontWeight: 600 }}>{profile.gpa}</div></div>}
                    {(profile.city || profile.state) && <div><div style={{ fontSize: 9, color: C.textDim, letterSpacing: 1, fontFamily: "'DM Mono',monospace" }}>LOCATION</div><div style={{ fontSize: 16, color: C.text, fontWeight: 600 }}>{[profile.city, profile.state ? (STATE_ABBR[profile.state] || profile.state) : ''].filter(Boolean).join(', ')}</div></div>}
                  </div>
                  {profile.highSchool && <div style={{ marginTop: 8 }}><div style={{ fontSize: 9, color: C.textDim, letterSpacing: 1, fontFamily: "'DM Mono',monospace", marginBottom: 2 }}>HIGH SCHOOL</div><div style={{ fontSize: 15, color: C.text, fontWeight: 600 }}>{profile.highSchool}</div></div>}
                  {profile.email && <div style={{ marginTop: 10, fontSize: 12, color: C.teal }}><a href={`mailto:${profile.email}`} style={{ color: C.teal, textDecoration: 'none' }}>✉ {profile.email}</a></div>}
                  {(profile.instagram || profile.twitter) && (
                    <div style={{ marginTop: 8, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                      {profile.instagram && (
                        <a href={`https://instagram.com/${profile.instagram}`} target="_blank" rel="noopener noreferrer" style={{ color: C.teal, textDecoration: 'none', fontSize: 12, display: 'flex', alignItems: 'center', gap: 4 }}>
                          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="13" height="13" style={{ flexShrink: 0 }}><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>
                          @{profile.instagram}
                        </a>
                      )}
                      {profile.twitter && (
                        <a href={`https://x.com/${profile.twitter}`} target="_blank" rel="noopener noreferrer" style={{ color: C.teal, textDecoration: 'none', fontSize: 12, display: 'flex', alignItems: 'center', gap: 4 }}>
                          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="13" height="13" style={{ flexShrink: 0 }}><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.744l7.737-8.835L1.254 2.25H8.08l4.253 5.622 5.91-5.622zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
                          @{profile.twitter}
                        </a>
                      )}
                    </div>
                  )}
                </div>

                {/* Committed school logo — right side of header */}
                {profile.committedSchool && (
                  <div style={{ flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, minWidth: 72 }}>
                    {profile.committedSchool.logoUrl ? (
                      <>
                        <img src={profile.committedSchool.logoUrl} alt={profile.committedSchool.name} width={60} height={60} style={{ objectFit: 'contain' }} onError={e => { e.target.style.display='none'; }} />
                        <div style={{ fontSize: 8, color: C.gold, letterSpacing: 1, fontFamily: "'DM Mono',monospace", textAlign: 'center' }}>COMMITTED</div>
                        <div style={{ fontSize: 10, color: C.text, textAlign: 'center', fontWeight: 600, maxWidth: 80, lineHeight: 1.2 }}>{profile.committedSchool.name}</div>
                      </>
                    ) : (
                      <>
                        <div style={{ fontSize: 8, padding: '2px 7px', background: C.gold+'22', border: `1px solid ${C.gold}55`, borderRadius: 2, color: C.gold, letterSpacing: 1, fontFamily: "'DM Mono',monospace" }}>COMMITTED</div>
                        <div style={{ fontSize: 12, color: C.text, textAlign: 'center', fontWeight: 600, maxWidth: 80, lineHeight: 1.3 }}>{profile.committedSchool.name}</div>
                        {profile.committedSchool.division && <div style={{ fontSize: 10, color: C.textDim }}>{profile.committedSchool.division}</div>}
                      </>
                    )}
                  </div>
                )}
                {/* Mini diamond — positions visualization */}
                {(profile.positions||[]).length > 0 && (
                  <div style={{ flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
                    <MiniDiamond positions={profile.positions||[]} />
                    <div style={{ fontSize: 8, color: C.textDim, letterSpacing: 1, fontFamily: "'DM Mono',monospace", textAlign: 'center' }}>POSITIONS</div>
                  </div>
                )}
              </div>
            </div>

            {/* Bio */}
            {profile.bio && (
              <div style={{ background: C.surface, border: `1px solid ${C.greenBorder}`, borderRadius: 8, padding: 18, marginBottom: 14 }}>
                <div style={{ fontSize: 10, color: C.gold, letterSpacing: 2, marginBottom: 8, fontWeight: 600, fontFamily: "'DM Mono',monospace" }}>ABOUT</div>
                <div style={{ fontSize: 14, color: C.textMid, lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>{profile.bio}</div>
              </div>
            )}

            {/* Stats — hidden when showStats === false */}
            {profile.showStats !== false && <div style={{ background: C.surface, border: `1px solid ${C.greenBorder}`, borderRadius: 8, padding: 18, marginBottom: 14 }}>
              <div style={{ fontSize: 10, color: C.gold, letterSpacing: 2, marginBottom: 12, fontWeight: 600, fontFamily: "'DM Mono',monospace" }}>STATS</div>
              {!hasStats ? (
                <div style={{ fontSize: 13, color: C.textDim, fontStyle: 'italic', lineHeight: 1.6 }}>Stats will appear here after import from GameChanger.</div>
              ) : (
                <>
                  {agesWithData.length > 1 && (
                    <div style={{ display: 'flex', gap: 5, marginBottom: 10, flexWrap: 'wrap' }}>
                      {agesWithData.map(ag => (
                        <button key={ag} className="stat-chip" onClick={() => { setStatsAg(ag); setStatsSeason(''); }} style={{ background: activeAg===ag?(AGE_GROUP_COLORS[ag]||C.teal)+'22':'transparent', color: activeAg===ag?(AGE_GROUP_COLORS[ag]||C.teal):C.textDim, borderColor: activeAg===ag?(AGE_GROUP_COLORS[ag]||C.teal)+'66':C.greenBorder }}>{ag}</button>
                      ))}
                    </div>
                  )}
                  {seasonsForAg.length > 1 && (
                    <div style={{ display: 'flex', gap: 5, marginBottom: 12, flexWrap: 'wrap' }}>
                      {seasonsForAg.map(sid => (
                        <button key={sid} className="stat-chip" onClick={() => setStatsSeason(sid)} style={{ background: activeSeason===sid?C.greenMid:'transparent', color: activeSeason===sid?C.gold:C.textDim, borderColor: activeSeason===sid?C.gold:C.greenBorder }}>{seasonLabel(sid)}</button>
                      ))}
                    </div>
                  )}
                  {seasonsForAg.length === 1 && <div style={{ fontSize: 12, color: C.textDim, marginBottom: 10 }}>{seasonLabel(activeSeason)} · {activeAg}</div>}

                  {/* Section tabs — only render when multiple sections have data */}
                  {[hasBatEver, hasPitchEver, hasFldEver].filter(Boolean).length > 1 && (
                    <div style={{ display: 'flex', gap: 0, marginBottom: 10, borderBottom: `1px solid ${C.greenBorder}` }}>
                      {hasBatEver && <button onClick={() => setStatsSection('batting')} style={{ padding: '5px 14px', fontSize: 10, letterSpacing: 1, fontFamily: "'DM Mono',monospace", cursor: 'pointer', border: 'none', borderBottom: `2px solid ${effectiveSection==='batting'?C.gold:'transparent'}`, background: 'transparent', color: effectiveSection==='batting'?C.gold:C.textDim, marginBottom: -1 }}>BATTING</button>}
                      {hasPitchEver && <button onClick={() => setStatsSection('pitching')} style={{ padding: '5px 14px', fontSize: 10, letterSpacing: 1, fontFamily: "'DM Mono',monospace", cursor: 'pointer', border: 'none', borderBottom: `2px solid ${effectiveSection==='pitching'?C.gold:'transparent'}`, background: 'transparent', color: effectiveSection==='pitching'?C.gold:C.textDim, marginBottom: -1 }}>PITCHING</button>}
                      {hasFldEver && <button onClick={() => setStatsSection('fielding')} style={{ padding: '5px 14px', fontSize: 10, letterSpacing: 1, fontFamily: "'DM Mono',monospace", cursor: 'pointer', border: 'none', borderBottom: `2px solid ${effectiveSection==='fielding'?C.gold:'transparent'}`, background: 'transparent', color: effectiveSection==='fielding'?C.gold:C.textDim, marginBottom: -1 }}>FIELDING</button>}
                    </div>
                  )}

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
                    {effectiveSection === 'batting' && BAT_FIELDS.map(f => {
                      const val = batting[f]; const isDerived = DERIVED_STATS.has(f);
                      return <div key={f} style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', borderBottom: `1px solid ${C.greenBorder}22` }}>
                        <span style={{ fontSize: 10, color: C.textDim, letterSpacing: 1 }}>{STAT_LABELS[f]||f}</span>
                        <span style={{ fontSize: 10, color: isDerived?C.gold:C.text, fontWeight: isDerived?600:400 }}>{fmtStatVal(f,val)}</span>
                      </div>;
                    })}
                    {effectiveSection === 'pitching' && PIT_FIELDS.map(f => {
                      const val = pitching[f]; const isDerived = DERIVED_STATS.has(f);
                      return <div key={f} style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', borderBottom: `1px solid ${C.greenBorder}22` }}>
                        <span style={{ fontSize: 10, color: C.textDim, letterSpacing: 1 }}>{STAT_LABELS[f]||f}</span>
                        <span style={{ fontSize: 10, color: isDerived?C.gold:C.text, fontWeight: isDerived?600:400 }}>{fmtStatVal(f,val)}</span>
                      </div>;
                    })}
                    {effectiveSection === 'fielding' && FLD_FIELDS.map(f => {
                      const val = fielding[f]; const isDerived = DERIVED_STATS.has(f);
                      return <div key={f} style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', borderBottom: `1px solid ${C.greenBorder}22` }}>
                        <span style={{ fontSize: 10, color: C.textDim, letterSpacing: 1 }}>{STAT_LABELS[f]||f}</span>
                        <span style={{ fontSize: 10, color: isDerived?C.gold:C.text, fontWeight: isDerived?600:400 }}>{fmtStatVal(f,val)}</span>
                      </div>;
                    })}
                  </div>
                  {(()=>{const ls=snaps.length?[...snaps].sort((a,b)=>(b.importedAt||'')>(a.importedAt||'')?1:-1)[0]:null;const dt=ls?.importedAt||profile.lastUpdated;return dt?<div style={{fontSize:9,color:C.textDim,marginTop:10,fontStyle:'italic'}}>Stats updated {new Date(dt).toLocaleDateString('en-US',{month:'short',day:'numeric',year:'numeric'})}</div>:null;})()}
                </>
              )}
            </div>}

            {/* Allister Index / ODM Testing — hidden when showOdm === false, same gating pattern as
                STATS above. Latest result headlines the card; every past dated result is listed below
                it (newest first) and expands to the full raw scorecard, so a recruiter can see
                progress over time, not just a snapshot. */}
            {profile.showOdm !== false && (profile.odmTests||[]).length > 0 && (() => {
              const tests = profile.odmTests; // already sorted newest-first by the Cloud Function
              const latest = tests[0];
              const fmtDt = d => d ? new Date(d+'T12:00:00').toLocaleDateString('en-US',{month:'short',day:'numeric',year:'numeric'}) : '';
              return (
                <div style={{ background: C.surface, border: `1px solid ${C.greenBorder}`, borderRadius: 8, padding: 18, marginBottom: 14 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
                    <div style={{ fontSize: 10, color: C.gold, letterSpacing: 2, fontWeight: 600, fontFamily: "'DM Mono',monospace" }}>ALLISTER INDEX</div>
                    <a href="https://odmtesting.com/" target="_blank" rel="noopener noreferrer" style={{ fontSize: 10, color: C.teal, textDecoration: 'none' }}>ℹ what is this?</a>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: tests.length > 1 ? 14 : 4 }}>
                    <div style={{ fontSize: 32, color: C.text, fontWeight: 700 }}>{latest.allisterIndex}</div>
                    <div style={{ fontSize: 11, color: C.textDim }}>{fmtDt(latest.date)}</div>
                  </div>
                  {tests.length > 1 && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      <div style={{ fontSize: 9, color: C.textDim, letterSpacing: 1, marginBottom: 2 }}>PAST RESULTS</div>
                      {tests.slice(1).map(t => {
                        const isOpen = odmExpandedId === t.id;
                        return (
                          <div key={t.id} style={{ background: C.bg, border: `1px solid ${C.greenBorder}33`, borderRadius: 4, padding: 10 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }} onClick={() => setOdmExpandedId(isOpen ? null : t.id)}>
                              <div style={{ flex: 1 }}>
                                <span style={{ fontSize: 14, color: C.text, fontWeight: 600 }}>{t.allisterIndex}</span>
                                <span style={{ fontSize: 10, color: C.textDim, marginLeft: 8 }}>{fmtDt(t.date)}</span>
                              </div>
                              <span style={{ fontSize: 10, color: C.textDim }}>{isOpen ? '▲' : '▼'}</span>
                            </div>
                            {isOpen && (
                              <div style={{ marginTop: 8, paddingTop: 8, borderTop: `1px solid ${C.greenBorder}33`, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 14px' }}>
                                {ODM_FIELDS.map(([k,lbl]) => t[k] !== undefined && (
                                  <div key={k} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10 }}>
                                    <span style={{ color: C.textDim }}>{lbl}</span><span style={{ color: C.textMid }}>{t[k]}</span>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Video Links */}
            {(profile.videoLinks||[]).length > 0 && (
              <div style={{ background: C.surface, border: `1px solid ${C.greenBorder}`, borderRadius: 8, padding: 18, marginBottom: 14 }}>
                <div style={{ fontSize: 10, color: C.gold, letterSpacing: 2, marginBottom: 12, fontWeight: 600, fontFamily: "'DM Mono',monospace" }}>VIDEO HIGHLIGHTS</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                  {profile.videoLinks.map((url, i) => {
                    const embed = getVideoEmbed(url);
                    if (!embed) return null;
                    return (
                      <div key={i}>
                        <div style={{ fontSize: 9, color: C.textDim, letterSpacing: 1, fontFamily: "'DM Mono',monospace", marginBottom: 6 }}>{embed.label} · Video {i + 1}</div>
                        {embed.type === 'iframe' ? (
                          <div style={{ position: 'relative', width: '100%', aspectRatio: '16/9', borderRadius: 8, overflow: 'hidden' }}>
                            <iframe src={embed.src} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 'none' }} allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen title={`Video ${i + 1}`} />
                          </div>
                        ) : (
                          <a href={url} target="_blank" rel="noopener noreferrer" style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', background: C.bg, border: `1px solid ${C.greenBorder}`, borderRadius: 4, textDecoration: 'none', color: C.teal, fontSize: 13 }}>
                            <span style={{ flexShrink: 0, fontSize: 16 }}>▶</span>
                            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>{url}</span>
                            <span style={{ fontSize: 10, color: C.textDim, flexShrink: 0 }}>↗</span>
                          </a>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Tournament History */}
            {(profile.tournamentHistory||[]).length > 0 && (()=>{
              const todayMs = new Date(); todayMs.setHours(0,0,0,0);
              const isPast = t => {
                const end = t.endDate || t.startDate;
                return end ? new Date(end+'T23:59:59') < todayMs : false;
              };
              const current = profile.tournamentHistory.filter(t => !isPast(t));
              const past = profile.tournamentHistory.filter(t => isPast(t));
              const TRow = ({t}) => (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 10px', background: C.bg, border: `1px solid ${C.greenBorder}`, borderRadius: 4 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, color: C.text, fontWeight: 500 }}>{t.tournamentName}</div>
                    <div style={{ fontSize: 10, color: C.textDim, marginTop: 1 }}>{t.teamName}{t.seasonLabel ? ` · ${t.seasonLabel}` : ''}</div>
                  </div>
                  <div style={{ flexShrink: 0, fontSize: 11, color: C.textDim, textAlign: 'right' }}>{fmtDateRange(t.startDate, t.endDate)}</div>
                </div>
              );
              return (
                <div style={{ background: C.surface, border: `1px solid ${C.greenBorder}`, borderRadius: 8, padding: 18, marginBottom: 14 }}>
                  <div style={{ fontSize: 10, color: C.gold, letterSpacing: 2, marginBottom: 4, fontWeight: 600, fontFamily: "'DM Mono',monospace" }}>TOURNAMENT HISTORY</div>
                  <div style={{ fontSize: 13, color: C.textDim, marginBottom: 12 }}>
                    {current.length > 0 && past.length > 0
                      ? <>{current.length} Future Tournament{current.length !== 1 ? 's' : ''} <span style={{ color: C.textDim, opacity: 0.7 }}>({past.length} Past)</span> with {orgName}</>
                      : current.length > 0
                        ? <>{current.length} Upcoming Tournament{current.length !== 1 ? 's' : ''} with {orgName}</>
                        : <>{past.length} Tournament{past.length !== 1 ? 's' : ''} with {orgName}</>
                    }
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {current.map((t, i) => <TRow key={i} t={t} />)}
                    {past.length > 0 && (
                      <div style={{ marginTop: current.length > 0 ? 6 : 0 }}>
                        <button onClick={() => setShowPastTournaments(v => !v)} style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'transparent', border: `1px solid ${C.greenBorder}44`, borderRadius: 4, padding: '7px 10px', cursor: 'pointer', color: C.textDim, fontFamily: 'inherit' }}>
                          <span style={{ fontSize: 11, color: C.textDim, letterSpacing: 1, fontFamily: "'DM Mono',monospace" }}>PAST TOURNAMENTS ({past.length})</span>
                          <span style={{ fontSize: 11, color: C.textDim }}>{showPastTournaments ? '▲' : '▼'}</span>
                        </button>
                        {showPastTournaments && (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 5, marginTop: 5, opacity: 0.75 }}>
                            {past.map((t, i) => <TRow key={i} t={t} />)}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })()}

            {/* Awards */}
            {(()=>{
              const allAwards=(profile.awards||[]).filter(a=>a.showOnProfile!==false);
              if(!allAwards.length)return null;
              const bySeason={};
              allAwards.forEach(a=>{if(!bySeason[a.seasonId])bySeason[a.seasonId]=[];bySeason[a.seasonId].push(a);});
              return(
                <div style={{ background: C.surface, border: `1px solid ${C.greenBorder}`, borderRadius: 8, padding: 18, marginBottom: 14 }}>
                  <div style={{ fontSize: 10, color: C.gold, letterSpacing: 2, marginBottom: 12, fontWeight: 600, fontFamily: "'DM Mono',monospace" }}>🏆 AWARDS</div>
                  {Object.entries(bySeason).map(([sid,awards])=>(
                    <div key={sid} style={{marginBottom:12}}>
                      <div style={{fontSize:9,color:C.textDim,letterSpacing:2,marginBottom:6,fontFamily:"'DM Mono',monospace"}}>{awards[0].seasonName||sid} · {awards[0].ageGroup}</div>
                      <div style={{display:"flex",flexDirection:"column",gap:5}}>
                        {awards.map(a=>(
                          <div key={a.awardId} style={{display:"flex",alignItems:"center",gap:10,padding:"7px 10px",background:C.bg,border:`1px solid ${C.gold}33`,borderRadius:4}}>
                            <span style={{fontSize:18,flexShrink:0}}>{a.emoji}</span>
                            <div>
                              <div style={{fontSize:13,color:C.text,fontWeight:500}}>{a.label}</div>
                              {a.statValue&&<div style={{fontSize:10,color:C.teal,fontFamily:"'DM Mono',monospace"}}>{a.statValue}</div>}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              );
            })()}

            {/* Share This Profile */}
            <div style={{ background: C.surface, border: `1px solid ${C.greenBorder}`, borderRadius: 8, padding: 18, marginBottom: 14 }}>
              <div style={{ fontSize: 10, color: C.gold, letterSpacing: 2, marginBottom: 14, fontWeight: 600, fontFamily: "'DM Mono',monospace" }}>SHARE THIS PROFILE</div>
              {/* Org QR row */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 18, flexWrap: 'wrap', marginBottom: profile.customQrUrl ? 16 : 0 }}>
                <div style={{ textAlign: 'center', flexShrink: 0 }}>
                  {qrSrc && <img src={qrSrc} width={100} height={100} alt="QR Code" style={{ display: 'block', borderRadius: 4, border: '3px solid #fff', objectFit: 'contain' }} />}
                  <div style={{ fontSize: 8, color: C.teal, marginTop: 4, fontFamily: "'DM Mono',monospace", letterSpacing: 0.5 }}>{orgName.toUpperCase()} QR</div>
                </div>
                <div style={{ flex: 1, minWidth: 180 }}>
                  <div style={{ fontSize: 9, color: C.textDim, letterSpacing: 1, marginBottom: 6, fontFamily: "'DM Mono',monospace" }}>{orgName.toUpperCase()} PROFILE</div>
                  <div style={{ fontSize: 11, color: C.textDim, marginBottom: 10, wordBreak: 'break-all', fontFamily: "'DM Mono',monospace" }}>{profileUrl}</div>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <button onClick={() => navigator.clipboard.writeText(profileUrl)} style={{ padding: '6px 12px', fontSize: 10, background: 'transparent', border: `1px solid ${C.teal}55`, borderRadius: 3, color: C.teal, cursor: 'pointer', fontFamily: "'DM Mono',monospace", letterSpacing: 1 }}>COPY LINK</button>
                    {qrSrc && <button onClick={downloadQr} style={{ padding: '6px 12px', fontSize: 10, background: 'transparent', border: `1px solid ${C.greenBorder}`, borderRadius: 3, color: C.textMid, cursor: 'pointer', fontFamily: "'DM Mono',monospace", letterSpacing: 1 }}>↓ DOWNLOAD QR</button>}
                  </div>
                </div>
              </div>
              {/* External QR row — only shown when uploaded */}
              {profile.customQrUrl && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 18, flexWrap: 'wrap', paddingTop: 14, borderTop: `1px solid ${C.greenBorder}33` }}>
                  <div style={{ textAlign: 'center', flexShrink: 0 }}>
                    <img src={profile.customQrUrl} width={100} height={100} alt="External QR" style={{ display: 'block', borderRadius: 4, border: '3px solid #fff', objectFit: 'contain' }} />
                    <div style={{ fontSize: 8, color: C.gold, marginTop: 4, fontFamily: "'DM Mono',monospace", letterSpacing: 0.5 }}>{profile.customQrLabel ? profile.customQrLabel.toUpperCase()+' QR' : 'EXTERNAL QR'}</div>
                  </div>
                  <div style={{ flex: 1, minWidth: 180 }}>
                    <div style={{ fontSize: 9, color: C.gold, letterSpacing: 1, marginBottom: 6, fontFamily: "'DM Mono',monospace" }}>{(profile.customQrLabel || 'EXTERNAL PROFILE').toUpperCase()}</div>
                    <div style={{ fontSize: 12, color: C.textDim, lineHeight: 1.6 }}>Scan to view {profile.customQrLabel ? `this athlete's ${profile.customQrLabel} recruiting profile` : 'external recruiting profile'}.</div>
                  </div>
                </div>
              )}
            </div>

            {(profile.org?.website || profile.org?.contactEmail) && (
              <div style={{ textAlign: 'center', padding: '4px 0', fontSize: 11, color: C.textDim }}>
                {profile.org.website && <a href={/^https?:\/\//.test(profile.org.website) ? profile.org.website : `https://${profile.org.website}`} target="_blank" rel="noopener noreferrer" style={{ color: C.teal, textDecoration: 'none' }}>{profile.org.website.replace(/^https?:\/\//, '')}</a>}
                {profile.org.website && profile.org.contactEmail && ' · '}
                {profile.org.contactEmail && <a href={`mailto:${profile.org.contactEmail}`} style={{ color: C.teal, textDecoration: 'none' }}>{profile.org.contactEmail}</a>}
              </div>
            )}
            <div style={{ textAlign: 'center', padding: '16px 0 8px', fontSize: 11, color: C.textDim }}>
              Powered by <a href="/" style={{ color: C.textDim, textDecoration: 'none' }}>{orgName}</a>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
